import { type FormEvent, useRef, useState } from "react";
import { toast } from "sonner";

import {
  useCarrierShipment,
  useCreateCarrierShipmentChangeRequest,
} from "@/api/carrier";
import { DetailDrawer } from "@/components/detail-drawer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { hasHttpStatus } from "@/lib/api-error";

interface RequestShipmentChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shipmentId: string | null;
  shipmentLabel: string;
}

const TYPES = [
  { value: "RECIPIENT_DELIVERY_INSTRUCTIONS", label: "Delivery instructions" },
  { value: "SENDER_PICKUP_INSTRUCTIONS", label: "Pickup instructions" },
  { value: "RECIPIENT_CONTACT_UPDATE", label: "Recipient contact" },
  { value: "SENDER_CONTACT_UPDATE", label: "Sender contact" },
  {
    value: "RECIPIENT_ADDRESS_CORRECTION",
    label: "Recipient Unit / apartment correction",
  },
  {
    value: "SENDER_ADDRESS_CORRECTION",
    label: "Sender Unit / apartment correction",
  },
] as const;

function validateDetail(
  type: string,
  value: string,
  removeUnit: boolean,
  clearInstructions: boolean,
  contact: { name: string; phone: string; email: string },
  clearContact: { name: boolean; phone: boolean; email: boolean }
): string | null {
  if (type.endsWith("CONTACT_UPDATE")) {
    if (
      !Object.values(contact).some(Boolean) &&
      !Object.values(clearContact).some(Boolean)
    )
      return "Enter or clear at least one contact field.";
    if (contact.phone) {
      const digits = contact.phone.replace(/\D/g, "");
      if (!/^[+0-9][0-9 ()-]*$/.test(contact.phone) || digits.length < 7)
        return "Enter a valid contact phone number.";
    }
    if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email))
      return "Enter a valid contact email.";
    return null;
  }
  if (
    !value &&
    !(type.endsWith("ADDRESS_CORRECTION") && removeUnit) &&
    !clearInstructions
  ) {
    return "Complete the requested change detail.";
  }
  return null;
}

export function RequestShipmentChangeDialog({
  open,
  onOpenChange,
  shipmentId,
  shipmentLabel,
}: RequestShipmentChangeDialogProps) {
  const create = useCreateCarrierShipmentChangeRequest();
  const shipment = useCarrierShipment(open ? shipmentId : null);
  const [type, setType] = useState<string>(TYPES[0].value);
  const [detail, setDetail] = useState("");
  const [notes, setNotes] = useState("");
  const [unitDraft, setUnitDraft] = useState<{
    key: string;
    value: string;
  } | null>(null);
  const [removeUnit, setRemoveUnit] = useState(false);
  const [clearInstructions, setClearInstructions] = useState(false);
  const [contact, setContact] = useState({ name: "", phone: "", email: "" });
  const [clearContact, setClearContact] = useState({
    name: false,
    phone: false,
    email: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lock = useRef(false);

  const isUnitCorrection = type.endsWith("ADDRESS_CORRECTION");
  const currentAddress = shipment.data?.addresses.find(
    (address) => address.type === (type.startsWith("SENDER_") ? "FROM" : "TO")
  );
  const currentUnit =
    currentAddress?.addressLine2 ?? currentAddress?.address?.line2 ?? "";
  const unitKey = `${shipmentId}:${type}`;
  const unitValue = unitDraft?.key === unitKey ? unitDraft.value : currentUnit;
  const unitReady =
    Boolean(currentAddress) && !shipment.isLoading && !shipment.error;
  const unitChanged =
    unitReady && (removeUnit ? "" : unitValue.trim()) !== currentUnit.trim();

  if (!shipmentId) return null;

  const detailLabel = type.endsWith("ADDRESS_CORRECTION")
    ? "Correct unit / apartment number"
    : "Instructions";

  const reset = () => {
    setType(TYPES[0].value);
    setDetail("");
    setUnitDraft(null);
    setNotes("");
    setRemoveUnit(false);
    setClearInstructions(false);
    setContact({ name: "", phone: "", email: "" });
    setClearContact({ name: false, phone: false, email: false });
    setError(null);
  };

  const close = () => {
    if (saving) return;
    reset();
    onOpenChange(false);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (lock.current || (isUnitCorrection && !unitChanged)) return;
    const normalizedDetail = (isUnitCorrection ? unitValue : detail).trim();
    const validationError = validateDetail(
      type,
      normalizedDetail,
      removeUnit,
      clearInstructions,
      contact,
      clearContact
    );
    if (validationError) {
      setError(validationError);
      return;
    }
    const input: Parameters<typeof create.mutateAsync>[0]["input"] = {
      requestTypes: [type],
      additionalNotes: notes.trim() || undefined,
    };
    if (type.endsWith("ADDRESS_CORRECTION"))
      input.addressLine2Correction = removeUnit ? "" : normalizedDetail;
    else if (type.endsWith("CONTACT_UPDATE")) {
      if (contact.name.trim() || clearContact.name)
        input.contactName = clearContact.name ? "" : contact.name.trim();
      if (contact.phone.trim() || clearContact.phone)
        input.contactPhone = clearContact.phone ? "" : contact.phone.trim();
      if (contact.email.trim() || clearContact.email)
        input.contactEmail = clearContact.email ? "" : contact.email.trim();
    } else
      input.deliveryInstructions = clearInstructions ? "" : normalizedDetail;

    lock.current = true;
    setSaving(true);
    setError(null);
    try {
      await create.mutateAsync({ shipmentId, input });
      toast.success(`Change request submitted for ${shipmentLabel}.`);
      reset();
      onOpenChange(false);
    } catch (caught) {
      const response = caught as {
        response?: { data?: { error?: string; message?: string } };
      };
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to request changes for this shipment."
        : (response.response?.data?.message ??
          response.response?.data?.error ??
          "Change request could not be submitted.");
      setError(message);
      toast.error(message);
    } finally {
      lock.current = false;
      setSaving(false);
    }
  };

  return (
    <DetailDrawer
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
      title={`Request change — ${shipmentLabel}`}
      description="Submitted for review before changes apply."
      widthClassName="sm:max-w-md"
    >
      <form noValidate onSubmit={handleSubmit} className="space-y-4">
        <Field label="Change type" id="cr-type">
          <NativeSelect
            id="cr-type"
            value={type}
            onChange={(event) => {
              setType(event.target.value);
              setDetail("");
              setUnitDraft(null);
              setRemoveUnit(false);
              setClearInstructions(false);
              setContact({ name: "", phone: "", email: "" });
              setClearContact({ name: false, phone: false, email: false });
              setError(null);
            }}
            disabled={saving}
          >
            {TYPES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </NativeSelect>
        </Field>

        {!type.endsWith("CONTACT_UPDATE") && (
          <Field label={detailLabel} id="cr-detail">
            <Input
              id="cr-detail"
              type="text"
              required={!clearInstructions && !removeUnit}
              maxLength={type.endsWith("ADDRESS_CORRECTION") ? 100 : 500}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? "cr-detail-error" : undefined}
              value={isUnitCorrection ? unitValue : detail}
              onChange={(event) => {
                if (isUnitCorrection)
                  setUnitDraft({ key: unitKey, value: event.target.value });
                else setDetail(event.target.value);
                setError(null);
              }}
              disabled={saving || (isUnitCorrection && !unitReady)}
              readOnly={
                (removeUnit && type.endsWith("ADDRESS_CORRECTION")) ||
                clearInstructions
              }
            />
          </Field>
        )}
        {type.endsWith("INSTRUCTIONS") && (
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id="cr-clear-instructions"
              checked={clearInstructions}
              onCheckedChange={(checked) =>
                setClearInstructions(checked === true)
              }
              disabled={saving}
            />
            <label htmlFor="cr-clear-instructions">
              Clear current instructions
            </label>
          </div>
        )}
        {type.endsWith("CONTACT_UPDATE") && (
          <div className="space-y-3">
            {(["name", "phone", "email"] as const).map((field) => (
              <div key={field} className="space-y-1">
                <Field label={`Contact ${field}`} id={`cr-contact-${field}`}>
                  <Input
                    id={`cr-contact-${field}`}
                    type={
                      field === "email"
                        ? "email"
                        : field === "phone"
                          ? "tel"
                          : "text"
                    }
                    value={contact[field]}
                    onChange={(event) =>
                      setContact((current) => ({
                        ...current,
                        [field]: event.target.value,
                      }))
                    }
                    readOnly={clearContact[field]}
                    disabled={saving}
                  />
                </Field>
                <div className="flex items-center gap-2 text-xs">
                  <Checkbox
                    id={`cr-clear-contact-${field}`}
                    checked={clearContact[field]}
                    onCheckedChange={(checked) =>
                      setClearContact((current) => ({
                        ...current,
                        [field]: checked === true,
                      }))
                    }
                    disabled={saving}
                  />
                  <label htmlFor={`cr-clear-contact-${field}`}>
                    Clear current contact {field}
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
        {type.endsWith("ADDRESS_CORRECTION") && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {shipment.isLoading
                ? "Loading current unit / apartment…"
                : !unitReady
                  ? "Current unit / apartment could not be loaded. Reopen this form to retry."
                  : `Current unit / apartment: ${currentUnit || "None"}`}
            </p>
            <div className="flex items-center gap-2 text-sm">
              <Checkbox
                id="cr-remove-unit"
                checked={removeUnit}
                onCheckedChange={(checked) => setRemoveUnit(checked === true)}
                disabled={saving || !unitReady}
              />
              <label htmlFor="cr-remove-unit">
                Remove the current unit / apartment number
              </label>
            </div>
            <p className="text-xs text-muted-foreground">
              Only unit / apartment changes can be submitted here. Contact
              support for street, city, or postal code changes.
            </p>
            {notes.trim() && !unitChanged && (
              <p className="text-xs text-muted-foreground">
                Notes alone do not change the unit. Edit the unit field above to
                submit.
              </p>
            )}
          </div>
        )}

        <Field label="Notes (optional)" id="cr-notes">
          <Textarea
            id="cr-notes"
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={saving}
          />
        </Field>

        {error && (
          <p
            id="cr-detail-error"
            role="alert"
            className="text-sm text-destructive"
          >
            {error}
          </p>
        )}
        {saving && (
          <p role="status" className="text-sm text-muted-foreground">
            Submitting change request…
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            size="touch"
            className="w-full sm:w-auto"
            onClick={close}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="touch"
            className="w-full sm:w-auto"
            disabled={saving || (isUnitCorrection && !unitChanged)}
          >
            {saving
              ? "Submitting…"
              : isUnitCorrection && unitReady && !unitChanged
                ? "No changes"
                : "Submit request"}
          </Button>
        </div>
      </form>
    </DetailDrawer>
  );
}

function Field({
  label,
  id,
  children,
}: {
  label: string;
  id: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {children}
    </div>
  );
}
